import { useCallback, useMemo, useState } from 'react'
import { DEFAULT_TAXI_LITRES_PER_10_MIN, DEFAULT_TAXI_MINUTES, workedWeightBalance, type WorkedWeightBalance } from '@jupi/shared'
import type { usePlanDraft } from './usePlanDraft'

// What the weight & balance pages assume about the flight, and what the
// pilot has changed. Each is a default taken from the plan or the aircraft
// until the pilot types over it; the overrides are kept for the session so
// the Weight page and the worked sheet show the same figures.

export interface WbOverrides {
  /** US gallons per hour. */
  flowUsgPerHour?: number
  flightMinutes?: number
  taxiMinutes?: number
  /** Fuel burned per 10 minutes of taxi, litres. */
  taxiLitresPerTenMin?: number
}

const KEY = 'jupi.wb.overrides'

function read(registration: string | null): WbOverrides {
  try {
    const all = JSON.parse(sessionStorage.getItem(KEY) ?? '{}') as Record<string, WbOverrides>
    return (registration && all[registration]) || {}
  } catch {
    return {}
  }
}

function write(registration: string | null, overrides: WbOverrides) {
  if (!registration) return
  try {
    const all = JSON.parse(sessionStorage.getItem(KEY) ?? '{}') as Record<string, WbOverrides>
    all[registration] = overrides
    sessionStorage.setItem(KEY, JSON.stringify(all))
  } catch {
    // Private window or blocked storage: the overrides last until the page closes.
  }
}

export interface WorkedWeight {
  worked: WorkedWeightBalance | null
  defaults: { flowUsgPerHour: number | null; flightMinutes: number; taxiMinutes: number; taxiLitresPerTenMin: number }
  overrides: WbOverrides
  setOverride: (change: Partial<Record<keyof WbOverrides, number | undefined>>) => void
  reset: () => void
}

export function useWorkedWeight(editor: ReturnType<typeof usePlanDraft>): WorkedWeight {
  const { load, figures, aircraft, draft } = editor
  const registration = draft?.registration ?? null
  const [stored, setStored] = useState<{ registration: string | null; overrides: WbOverrides }>(() => ({
    registration,
    overrides: read(registration),
  }))
  // The plan's aircraft arrives after the first render.
  const overrides = stored.registration === registration ? stored.overrides : read(registration)

  const setOverride = useCallback(
    (change: Partial<Record<keyof WbOverrides, number | undefined>>) => {
      const next = { ...overrides, ...change }
      for (const key of Object.keys(next) as (keyof WbOverrides)[]) if (next[key] === undefined) delete next[key]
      write(registration, next)
      setStored({ registration, overrides: next })
    },
    [overrides, registration],
  )
  const reset = useCallback(() => {
    write(registration, {})
    setStored({ registration, overrides: {} })
  }, [registration])

  const performance = aircraft?.performance ?? null
  const defaults = {
    flowUsgPerHour: aircraft?.fuelConsumptionUsg ?? null,
    flightMinutes: Math.round(figures?.totals.minutes ?? 0),
    taxiMinutes: performance?.taxiMinutes ?? DEFAULT_TAXI_MINUTES,
    taxiLitresPerTenMin: performance?.taxiLitresPerTenMin ?? DEFAULT_TAXI_LITRES_PER_10_MIN,
  }

  const worked = useMemo(
    () =>
      load && aircraft && performance && registration
        ? workedWeightBalance(
            { ...performance, fuelType: aircraft.fuelType, mtowKg: aircraft.mtowKg },
            load,
            {
              flowUsgPerHour: overrides.flowUsgPerHour ?? defaults.flowUsgPerHour ?? 0,
              flightMinutes: overrides.flightMinutes ?? defaults.flightMinutes,
              taxiMinutes: overrides.taxiMinutes ?? defaults.taxiMinutes,
              taxiLitresPerTenMin: overrides.taxiLitresPerTenMin ?? defaults.taxiLitresPerTenMin,
            },
          )
        : null,
    // defaults are derived from the same inputs listed here
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [load, aircraft, performance, registration, overrides, defaults.flowUsgPerHour, defaults.flightMinutes, defaults.taxiMinutes, defaults.taxiLitresPerTenMin],
  )

  return { worked, defaults, overrides, setOverride, reset }
}
